import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Person as PersonIcon,
  Badge as BadgeIcon,
  Work as WorkIcon,
  LocalHospital as HealthIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  RadioButtonChecked as RadioCheckedIcon,
  RadioButtonUnchecked as RadioUncheckedIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxBlankIcon,
  AccessTime as AccessTimeIcon,
  InfoOutlined as InfoIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import AttendanceStepper from "./components/AttendanceStepper";
import AttendanceInfo from "./components/AttendanceInfo";
import { useAttendance } from "../../hooks/useAttendance";
import "../../assets/scss/pages/AttendanceQR.scss";

const AttendanceQR = () => {
  const {
    isLoading,
    canProceed,
    tokenError,
    userData,
    isRegistered,
    lastRecord,
    currentStep,
    recordType,
    formData,
    exitFeedback,
    searchUser,
    updateFormData,
    updateExitFeedback,
    shouldShowExitFeedback,
    shouldShowInfo,
    nextStep,
    previousStep,
    submitAttendance,
    isStaff,
    visitReasons,
    termsAccepted,
    setTermsAccepted,
    hasSearched,
    previousVisitorData,
  } = useAttendance();

  const [documentNumber, setDocumentNumber] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Función para determinar si un campo debe estar deshabilitado
  const isFieldDisabled = (fieldName) => {
    if (!isRegistered || !userData) return false;

    // Mapeo de campos del formulario a campos del userData
    const fieldMapping = {
      name: () => userData.firstName && userData.lastName,
      eps: () => userData.eps,
      jobPosition: () => userData.jobPosition,
      arl: () => userData.arl,
      emergencyPhone: () => userData.emergencyPhone,
    };

    return fieldMapping[fieldName] ? fieldMapping[fieldName]() : false;
  };

  // Función para determinar si hay campos incompletos
  const hasIncompleteFields = () => {
    if (!isRegistered || !userData) return false;

    const requiredFields = ["eps", "jobPosition", "arl", "emergencyPhone"];
    return requiredFields.some((field) => !userData[field]);
  };

  // Función para obtener el valor del campo con datos previos
  const getFieldValue = (fieldName) => {
    // Para usuarios registrados
    if (isRegistered && userData) {
      switch (fieldName) {
        case "name":
          return userData.firstName && userData.lastName
            ? `${userData.firstName} ${userData.lastName}`
            : formData.name || "";
        case "eps":
          return userData.eps || formData.eps || "";
        case "jobPosition":
          return userData.jobPosition || formData.jobPosition || "";
        case "arl":
          return userData.arl || formData.arl || "";
        case "emergencyPhone":
          return userData.emergencyPhone || formData.emergencyPhone || "";
        default:
          return formData[fieldName] || "";
      }
    }

    // Para visitantes recurrentes no registrados
    if (!isRegistered && previousVisitorData) {
      // Si es salida, siempre usar datos previos sin permitir edición
      if (recordType === "salida") {
        switch (fieldName) {
          case "name":
            return previousVisitorData.name || formData.name || "";
          case "phone":
            return previousVisitorData.phone || formData.phone || "";
          case "eps":
            return previousVisitorData.eps || formData.eps || "";
          case "jobPosition":
            return (
              previousVisitorData.jobPosition || formData.jobPosition || ""
            );
          case "arl":
            return previousVisitorData.arl || formData.arl || "";
          case "emergencyPhone":
            return (
              previousVisitorData.emergencyPhone ||
              formData.emergencyPhone ||
              ""
            );
          case "company":
            return previousVisitorData.company || formData.company || "";
          case "visitorType":
            return (
              previousVisitorData.visitorType || formData.visitorType || ""
            );
          case "visitArea":
            return previousVisitorData.visitArea || formData.visitArea || "";
          default:
            return formData[fieldName] || "";
        }
      }

      // Si es entrada, permitir edición
      switch (fieldName) {
        case "name":
          return formData.name || previousVisitorData.name || "";
        case "phone":
          return formData.phone || previousVisitorData.phone || "";
        case "eps":
          return formData.eps || previousVisitorData.eps || "";
        case "jobPosition":
          return formData.jobPosition || previousVisitorData.jobPosition || "";
        case "arl":
          return formData.arl || previousVisitorData.arl || "";
        case "emergencyPhone":
          return (
            formData.emergencyPhone || previousVisitorData.emergencyPhone || ""
          );
        case "company":
          return formData.company || previousVisitorData.company || "";
        case "visitorType":
          return formData.visitorType || previousVisitorData.visitorType || "";
        case "visitArea":
          return formData.visitArea || previousVisitorData.visitArea || "";
        default:
          return formData[fieldName] || "";
      }
    }

    // Sin datos previos
    return formData[fieldName] || "";
  };

  // Definir pasos dinámicamente basados en el flujo
  const getSteps = () => {
    const steps = [
      {
        id: "entry",
        title: hasSearched
          ? recordType === "entrada"
            ? "Entrada"
            : "Salida"
          : "Búsqueda",
        description: hasSearched ? "Registro de asistencia" : "Buscar usuario",
      },
    ];

    // Solo agregar más pasos si ya se realizó la búsqueda
    if (hasSearched) {
      // Agregar paso de motivo si aplica
      if (
        recordType === "entrada" &&
        !isStaff &&
        formData.visitorType !== "personal"
      ) {
        steps.push({
          id: "reason",
          title: "Motivo",
          description: "Razón de la visita",
        });
      }

      // Agregar paso de información si aplica
      if (shouldShowInfo()) {
        steps.push({
          id: "info",
          title: "Información",
          description: "Información HSEQ-SV",
        });
      }

      // Agregar feedback de salida si aplica
      if (shouldShowExitFeedback()) {
        steps.push({
          id: "feedback",
          title: "Feedback",
          description: "Evaluación de visita",
        });
      }

      // Siempre agregar términos al final
      steps.push({
        id: "terms",
        title: "Términos",
        description: "Términos y condiciones",
      });
    }

    return steps;
  };

  const steps = getSteps();

  // Manejar búsqueda de documento
  const handleDocumentSearch = async () => {
    if (!documentNumber || documentNumber.length < 5) {
      setFormErrors({
        documentNumber: "Ingrese un número de documento válido",
      });
      return;
    }

    setSearchLoading(true);
    setFormErrors({});

    try {
      await searchUser(documentNumber);
    } finally {
      setSearchLoading(false);
    }
  };

  // Validar formulario según el paso actual
  const validateCurrentStep = () => {
    const errors = {};

    switch (currentStep) {
      case "entry":
        if (!hasSearched) {
          // En fase de búsqueda, no validar más campos
          return true;
        }

        if (!formData.documentNumber) {
          errors.documentNumber = "El número de documento es requerido";
        }

        if (!isRegistered) {
          if (!formData.name) errors.name = "El nombre es requerido";
          if (!formData.visitorType)
            errors.visitorType = "Seleccione el tipo de visitante";
          if (!formData.phone) errors.phone = "El teléfono es requerido";

          if (formData.visitorType && formData.visitorType !== "personal") {
            if (!formData.visitArea)
              errors.visitArea = "El área a visitar es requerida";
          }
        }
        break;

      case "reason":
        if (!formData.visitReason) {
          errors.visitReason = "Seleccione el motivo de la visita";
        }
        if (formData.visitReason === "otro" && !formData.visitReasonOther) {
          errors.visitReasonOther = "Especifique el motivo";
        }
        break;

      case "feedback":
        if (exitFeedback.purposeFulfilled === null) {
          errors.purposeFulfilled = "Esta pregunta es requerida";
        }
        if (exitFeedback.areaMetExpectations === null) {
          errors.areaMetExpectations = "Esta pregunta es requerida";
        }
        break;

      case "terms":
        if (!termsAccepted) {
          errors.terms = "Debe aceptar los términos y condiciones";
        }
        break;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Manejar avance al siguiente paso
  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep === "terms") {
        submitAttendance();
      } else {
        nextStep();
      }
    }
  };

  // Manejar cambio de input con control de estado
  const handleInputChange = (fieldName, value) => {
    // Solo actualizar si el campo no está deshabilitado
    if (!isFieldDisabled(fieldName)) {
      updateFormData({ [fieldName]: value });
    }
  };

  // Renderizar contenido según el paso actual
  const renderStepContent = () => {
  // Funciones auxiliares para manejo de campos
  const isFieldEditableForVisitor = (fieldName) => {
    if (isRegistered) {
      // Para usuarios registrados, usar la lógica existente
      return !isFieldDisabled(fieldName);
    }
    
    // Para visitantes
    if (recordType === 'salida') {
      // En salida, todos los campos son de solo lectura
      return false;
    }
    
    // En entrada, los campos son editables
    return true;
  };

  const getFieldStatusLabel = (fieldName) => {
    if (isRegistered && isFieldDisabled(fieldName)) {
      return <span className="field-status locked">🔒 Bloqueado</span>;
    }
    
    if (isRegistered && !isFieldDisabled(fieldName)) {
      return <span className="field-status editable">✏️ Editable</span>;
    }
    
    if (!isRegistered && previousVisitorData?.[fieldName]) {
      if (recordType === 'entrada') {
        return <span className="field-status editable">✏️ Editable</span>;
      } else {
        return <span className="field-status locked">🔒 Solo lectura</span>;
      }
    }
    
    return null;
  };

  const getFieldClassName = (fieldName, hasError = false) => {
    const baseClass = 'form-input';
    const errorClass = hasError ? 'error' : '';
    const lockedClass = !isFieldEditableForVisitor(fieldName) ? 'locked' : '';
    
    return `${baseClass} ${errorClass} ${lockedClass}`.trim();
  };

  const handleFieldChange = (fieldName, value) => {
    if (isFieldEditableForVisitor(fieldName)) {
      if (isRegistered) {
        handleInputChange(fieldName, value);
      } else {
        updateFormData({ [fieldName]: value });
      }
    }
  };

  switch (currentStep) {
    case "entry":
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="step-content entry-step"
        >
          <div className="step-header">
            <h2>
              {!hasSearched
                ? "Búsqueda de Usuario"
                : recordType === "entrada"
                ? "Registro de Entrada"
                : "Registro de Salida"}
            </h2>
            <p>
              {!hasSearched
                ? "Ingrese el número de documento para continuar"
                : "Complete la información para registrar su asistencia"}
            </p>
          </div>

          {/* Campo de búsqueda de documento */}
          <div className="document-search">
            <div className="form-group">
              <label>Número de Documento *</label>
              <div className="input-with-button">
                <div className="input-wrapper">
                  <BadgeIcon className="input-icon" />
                  <input
                    type="text"
                    className={`form-input ${
                      formErrors.documentNumber ? "error" : ""
                    }`}
                    placeholder="Ingrese su número de documento"
                    value={documentNumber}
                    onChange={(e) => setDocumentNumber(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && !userData && handleDocumentSearch()
                    }
                    disabled={userData !== null}
                    readOnly={userData !== null}
                  />
                </div>
                {!userData && (
                  <button
                    type="button"
                    className="btn btn-primary search-btn"
                    onClick={handleDocumentSearch}
                    disabled={searchLoading}
                  >
                    {searchLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Buscando...
                      </>
                    ) : (
                      <>
                        <SearchIcon />
                        Buscar
                      </>
                    )}
                  </button>
                )}
              </div>
              {formErrors.documentNumber && (
                <span className="error-message">
                  {formErrors.documentNumber}
                </span>
              )}
            </div>
          </div>

          {/* Mostrar información del usuario o formulario de registro */}
          {userData !== null && hasSearched && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="user-form-section"
            >
              <div className="section-title">
                <h3>
                  {isRegistered
                    ? "Datos Personales"
                    : "Registro de Visitante"}
                </h3>
                {isRegistered && (
                  <span className="user-badge">{userData.role}</span>
                )}
              </div>

              {/* Mensaje para visitantes recurrentes */}
              {!isRegistered && previousVisitorData && (
                <div
                  className={`alert alert-info recurring-visitor ${
                    recordType === "salida" ? "farewell" : ""
                  }`}
                >
                  <InfoIcon />
                  <div>
                    {recordType === "entrada" ? (
                      <>
                        <h4>¡Bienvenido de vuelta!</h4>
                        <p>
                          Hemos encontrado información de su última visita el{" "}
                          {new Date(
                            previousVisitorData.lastVisit
                          ).toLocaleDateString("es-CO")}
                          . Por favor, verifique y actualice si es necesario.
                        </p>
                      </>
                    ) : (
                      <>
                        <h4>¡Gracias por su visita!</h4>
                        <p>
                          Esperamos que su experiencia en nuestras
                          instalaciones haya sido satisfactoria. A
                          continuación, verificaremos sus datos de salida.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Mensaje de advertencia si no registró salida */}
              {lastRecord &&
                !lastRecord.hasExitRecord &&
                recordType === "entrada" && (
                  <div className="alert alert-warning">
                    <WarningIcon />
                    <p>
                      La última vez usted ingresó a las instalaciones y al
                      salir no notificó la salida. Por favor, recuerde
                      escanear el QR al salir.
                    </p>
                  </div>
                )}

              {/* Mensaje para campos con información existente vs campos editables */}
              {isRegistered && hasIncompleteFields() && (
                <div className="alert alert-info fields-completion-warning">
                  <InfoIcon />
                  <div className="warning-content">
                    <h4>Información de Usuario Registrado</h4>
                    <p>
                      Actualmente estamos en proceso de almacenar la
                      información de los usuarios que forman parte de la
                      familia de CTPS. Los campos que aparecen bloqueados ya
                      contienen su información almacenada, mientras que los
                      campos vacíos deben ser llenados manualmente.
                    </p>
                  </div>
                </div>
              )}

              <div className="form-grid">
                {/* Nombre */}
                <div className="form-group">
                  <label>
                    Nombre
                    {getFieldStatusLabel('name')}
                  </label>
                  <div className="input-wrapper">
                    <PersonIcon className="input-icon" />
                    <input
                      type="text"
                      className={getFieldClassName('name', formErrors.name)}
                      value={getFieldValue('name')}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      disabled={!isFieldEditableForVisitor('name')}
                      readOnly={!isFieldEditableForVisitor('name')}
                      placeholder={
                        !isRegistered && recordType === 'entrada' 
                          ? "Ingrese su nombre completo" 
                          : ""
                      }
                    />
                  </div>
                  {!isRegistered && recordType === 'entrada' && formErrors.name && (
                    <span className="error-message">{formErrors.name}</span>
                  )}
                </div>

                {/* Tipo de visitante (solo para no registrados) */}
                {!isRegistered && (
                  <div className="form-group">
                    <label>Tipo de Ingreso *</label>
                    <div className="select-wrapper">
                      <select
                        className={getFieldClassName('visitorType', formErrors.visitorType)}
                        value={getFieldValue("visitorType")}
                        onChange={(e) =>
                          isFieldEditableForVisitor('visitorType') && 
                          updateFormData({ visitorType: e.target.value })
                        }
                        disabled={!isFieldEditableForVisitor('visitorType')}
                      >
                        <option value="">Seleccionar</option>
                        <option value="visitante">Visitante</option>
                        <option value="personal">Personal</option>
                        <option value="domiciliario">Domiciliario</option>
                        <option value="socio">Socio</option>
                      </select>
                    </div>
                    {recordType === 'entrada' && formErrors.visitorType && (
                      <span className="error-message">
                        {formErrors.visitorType}
                      </span>
                    )}
                  </div>
                )}

                {/* EPS */}
                <div className="form-group">
                  <label>
                    EPS
                    {getFieldStatusLabel('eps')}
                  </label>
                  <div className="input-wrapper">
                    <HealthIcon className="input-icon" />
                    <input
                      type="text"
                      className={getFieldClassName('eps')}
                      value={getFieldValue('eps')}
                      onChange={(e) => handleFieldChange('eps', e.target.value)}
                      disabled={!isFieldEditableForVisitor('eps')}
                      readOnly={!isFieldEditableForVisitor('eps')}
                      placeholder={
                        isFieldEditableForVisitor('eps') ? "Ingrese su EPS" : ""
                      }
                    />
                  </div>
                </div>

                {/* Cargo */}
                <div className="form-group">
                  <label>
                    Cargo
                    {getFieldStatusLabel('jobPosition')}
                  </label>
                  <div className="input-wrapper">
                    <WorkIcon className="input-icon" />
                    <input
                      type="text"
                      className={getFieldClassName('jobPosition')}
                      value={getFieldValue('jobPosition')}
                      onChange={(e) => handleFieldChange('jobPosition', e.target.value)}
                      disabled={!isFieldEditableForVisitor('jobPosition')}
                      readOnly={!isFieldEditableForVisitor('jobPosition')}
                      placeholder={
                        isFieldEditableForVisitor('jobPosition')
                          ? "Cargo o posición"
                          : ""
                      }
                    />
                  </div>
                </div>

                {/* ARL */}
                <div className="form-group">
                  <label>
                    ARL
                    {getFieldStatusLabel('arl')}
                  </label>
                  <div className="input-wrapper">
                    <BusinessIcon className="input-icon" />
                    <input
                      type="text"
                      className={getFieldClassName('arl')}
                      value={getFieldValue('arl')}
                      onChange={(e) => handleFieldChange('arl', e.target.value)}
                      disabled={!isFieldEditableForVisitor('arl')}
                      readOnly={!isFieldEditableForVisitor('arl')}
                      placeholder={
                        isFieldEditableForVisitor('arl') ? "ARL afiliada" : ""
                      }
                    />
                  </div>
                </div>

                {/* Contacto de emergencia */}
                <div className="form-group">
                  <label>
                    Contacto de Emergencia
                    {getFieldStatusLabel('emergencyPhone')}
                  </label>
                  <div className="input-wrapper">
                    <PhoneIcon className="input-icon" />
                    <input
                      type="text"
                      className={getFieldClassName('emergencyPhone')}
                      value={getFieldValue('emergencyPhone')}
                      onChange={(e) => handleFieldChange('emergencyPhone', e.target.value)}
                      disabled={!isFieldEditableForVisitor('emergencyPhone')}
                      readOnly={!isFieldEditableForVisitor('emergencyPhone')}
                      placeholder={
                        isFieldEditableForVisitor('emergencyPhone')
                          ? "Teléfono de emergencia"
                          : ""
                      }
                    />
                  </div>
                </div>

                {/* Teléfono (solo para no registrados) */}
                {!isRegistered && (
                  <div className="form-group">
                    <label>Teléfono *</label>
                    <div className="input-wrapper">
                      <PhoneIcon className="input-icon" />
                      <input
                        type="text"
                        className={getFieldClassName('phone', formErrors.phone)}
                        value={getFieldValue('phone')}
                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                        disabled={!isFieldEditableForVisitor('phone')}
                        readOnly={!isFieldEditableForVisitor('phone')}
                        placeholder={
                          isFieldEditableForVisitor('phone') 
                            ? "Número de contacto" 
                            : ""
                        }
                      />
                    </div>
                    {recordType === 'entrada' && formErrors.phone && (
                      <span className="error-message">
                        {formErrors.phone}
                      </span>
                    )}
                  </div>
                )}

                {/* Empresa (solo para no registrados) */}
                {!isRegistered && (
                  <div className="form-group">
                    <label>Empresa</label>
                    <div className="input-wrapper">
                      <BusinessIcon className="input-icon" />
                      <input
                        type="text"
                        className={getFieldClassName('company')}
                        value={getFieldValue('company')}
                        onChange={(e) => handleFieldChange('company', e.target.value)}
                        disabled={!isFieldEditableForVisitor('company')}
                        readOnly={!isFieldEditableForVisitor('company')}
                        placeholder={
                          isFieldEditableForVisitor('company')
                            ? "Empresa que representa"
                            : ""
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Área a visitar (solo para visitantes no personal) */}
                {!isRegistered &&
                  formData.visitorType &&
                  formData.visitorType !== "personal" && (
                    <div className="form-group full-width">
                      <label>Área a Visitar *</label>
                      <div className="input-wrapper">
                        <LocationIcon className="input-icon" />
                        <input
                          type="text"
                          className={getFieldClassName('visitArea', formErrors.visitArea)}
                          value={getFieldValue('visitArea')}
                          onChange={(e) => handleFieldChange('visitArea', e.target.value)}
                          disabled={!isFieldEditableForVisitor('visitArea')}
                          readOnly={!isFieldEditableForVisitor('visitArea')}
                          placeholder={
                            isFieldEditableForVisitor('visitArea')
                              ? "Área o departamento"
                              : ""
                          }
                        />
                      </div>
                      {recordType === 'entrada' && formErrors.visitArea && (
                        <span className="error-message">
                          {formErrors.visitArea}
                        </span>
                      )}
                    </div>
                  )}
              </div>

              {/* Mensaje para personal no registrado */}
              {!isRegistered && formData.visitorType === "personal" && (
                <div className="alert alert-warning">
                  <p>
                    Actualmente estamos trabajando para integrar todo nuestro
                    personal a la plataforma designando sus roles y su
                    asistencia. Esperamos y entienda nuestro procedimiento.
                  </p>
                  <p className="warning-text">
                    Si no forma parte del personal, recuerde que la
                    información está siendo registrada y monitoreada. Esto
                    puede generar llamados de atención.
                  </p>
                </div>
              )}

              {/* Datos de salida aproximada (solo entrada) */}
              {recordType === "entrada" && (
                <div className="exit-time-section">
                  <h4>Salida (aproximada)</h4>
                  <div className="form-group">
                    <label>Hora</label>
                    <div className="input-wrapper">
                      <AccessTimeIcon className="input-icon" />
                      <input
                        type="time"
                        className="form-input"
                        value={formData.expectedExitTime}
                        onChange={(e) =>
                          updateFormData({ expectedExitTime: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Botón de siguiente */}
          {hasSearched && (
            <div className="step-actions">
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={handleNext}
                disabled={!userData || Object.keys(formErrors).length > 0}
              >
                Siguiente
              </button>
            </div>
          )}
        </motion.div>
      );

    case "reason":
      return (
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          className="step-content reason-step"
        >
          <div className="step-header">
            <h2>Motivo de Ingreso</h2>
            <p>Por favor, seleccione el motivo de su visita</p>
          </div>

          <div className="form-group">
            <label>Área de ingreso</label>
            <div className="input-wrapper">
              <LocationIcon className="input-icon" />
              <input
                type="text"
                className="form-input"
                value={formData.visitArea || "Logística"}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="reason-section">
            <h3>Razón de Ingreso</h3>
            <div className="reason-grid">
              {visitReasons.map((reason) => (
                <label key={reason.value} className="reason-option">
                  <input
                    type="radio"
                    name="visitReason"
                    value={reason.value}
                    checked={formData.visitReason === reason.value}
                    onChange={(e) =>
                      updateFormData({ visitReason: e.target.value })
                    }
                  />
                  <div className="reason-content">
                    {formData.visitReason === reason.value ? (
                      <RadioCheckedIcon className="radio-icon checked" />
                    ) : (
                      <RadioUncheckedIcon className="radio-icon" />
                    )}
                    <span>{reason.label}</span>
                  </div>
                </label>
              ))}
            </div>
            {formErrors.visitReason && (
              <span className="error-message">{formErrors.visitReason}</span>
            )}
          </div>

          {formData.visitReason === "otro" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="other-reason"
            >
              <div className="form-group">
                <label>Especifique el motivo</label>
                <div className="textarea-wrapper">
                  <DescriptionIcon className="input-icon" />
                  <textarea
                    className={`form-textarea ${
                      formErrors.visitReasonOther ? "error" : ""
                    }`}
                    rows={3}
                    placeholder="Describa el motivo de su visita"
                    value={formData.visitReasonOther}
                    onChange={(e) =>
                      updateFormData({ visitReasonOther: e.target.value })
                    }
                  />
                </div>
                {formErrors.visitReasonOther && (
                  <span className="error-message">
                    {formErrors.visitReasonOther}
                  </span>
                )}
              </div>
            </motion.div>
          )}

          <div className="step-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={previousStep}
            >
              Atrás
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNext}
            >
              Siguiente
            </button>
          </div>
        </motion.div>
      );

    case "info":
      return <AttendanceInfo onComplete={nextStep} onBack={previousStep} />;

    case "feedback":
      return (
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          className="step-content feedback-step"
        >
          <div className="step-header">
            <h2>¡Esperamos que todo haya salido bien en su visita!</h2>
            <p>Por favor, responda las siguientes preguntas:</p>
          </div>

          <div className="feedback-questions">
            <div className="question-group">
              <h4>1. ¿Se cumplió el propósito por el cual nos visitó?</h4>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="purposeFulfilled"
                    value="true"
                    checked={exitFeedback.purposeFulfilled === true}
                    onChange={() =>
                      updateExitFeedback({ purposeFulfilled: true })
                    }
                  />
                  <span>Sí</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="purposeFulfilled"
                    value="false"
                    checked={exitFeedback.purposeFulfilled === false}
                    onChange={() =>
                      updateExitFeedback({ purposeFulfilled: false })
                    }
                  />
                  <span>No</span>
                </label>
              </div>
              {formErrors.purposeFulfilled && (
                <span className="error-message">
                  {formErrors.purposeFulfilled}
                </span>
              )}
            </div>

            <div className="question-group">
              <h4>2. ¿El área que visitó cumplió con sus expectativas?</h4>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="areaMetExpectations"
                    value="true"
                    checked={exitFeedback.areaMetExpectations === true}
                    onChange={() =>
                      updateExitFeedback({ areaMetExpectations: true })
                    }
                  />
                  <span>Sí</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="areaMetExpectations"
                    value="false"
                    checked={exitFeedback.areaMetExpectations === false}
                    onChange={() =>
                      updateExitFeedback({ areaMetExpectations: false })
                    }
                  />
                  <span>No</span>
                </label>
              </div>
              {formErrors.areaMetExpectations && (
                <span className="error-message">
                  {formErrors.areaMetExpectations}
                </span>
              )}
            </div>

            <div className="question-group">
              <h4>3. Comentarios adicionales</h4>
              <textarea
                className="form-textarea"
                rows={4}
                placeholder="Espacio para observaciones, sugerencias o comentarios que desee compartir"
                value={exitFeedback.additionalComments}
                onChange={(e) =>
                  updateExitFeedback({ additionalComments: e.target.value })
                }
              />
            </div>

            <div className="privacy-section">
              <h3>Protección de Datos Personales</h3>
              <p>
                En cumplimiento de la Ley 1581 de 2012 (Ley de Protección de
                Datos Personales) y sus decretos reglamentarios, y con el fin
                de proteger su privacidad, le informamos que sus respuestas
                pueden ser utilizadas para evaluar y mejorar nuestros
                servicios.
              </p>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={exitFeedback.anonymousProcessing}
                  onChange={(e) =>
                    updateExitFeedback({
                      anonymousProcessing: e.target.checked,
                    })
                  }
                />
                <span>
                  Deseo que mis respuestas sean procesadas de forma anónima
                </span>
              </label>
            </div>
          </div>

          <div className="step-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={previousStep}
            >
              Atrás
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNext}
            >
              Siguiente
            </button>
          </div>
        </motion.div>
      );

    case "terms":
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="step-content terms-step"
        >
          <div className="step-header">
            <h2>Términos y Condiciones</h2>
            <p>Por favor, lea y acepte nuestros términos</p>
          </div>

          <div className="terms-content">
            <div className="terms-box">
              <h3>Política de Privacidad y Tratamiento de Datos</h3>
              <p>
                Al registrar su asistencia, usted acepta que CootransPetrols
                recopile y procese sus datos personales de acuerdo con la Ley
                1581 de 2012 y demás normas aplicables para:
              </p>
              <ul>
                <li>Control de acceso y seguridad</li>
                <li>Gestión de emergencias</li>
                <li>Mejora continua de nuestros servicios</li>
                <li>Cumplimiento de obligaciones legales</li>
              </ul>
              <p>
                Sus datos serán tratados con confidencialidad y no serán
                compartidos con terceros sin su consentimiento, salvo
                requerimiento legal.
              </p>
            </div>

            <div className="terms-acceptance">
              <label className="checkbox-label">
                {termsAccepted ? (
                  <CheckBoxIcon className="checkbox-icon checked" />
                ) : (
                  <CheckBoxBlankIcon className="checkbox-icon" />
                )}
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="hidden-checkbox"
                />
                <span>
                  Acepto los términos y condiciones de CootransPetrols
                </span>
              </label>
              {formErrors.terms && (
                <span className="error-message">{formErrors.terms}</span>
              )}
            </div>
          </div>

          <div className="step-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={previousStep}
            >
              Atrás
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNext}
              disabled={!termsAccepted}
            >
              Finalizar
            </button>
          </div>
        </motion.div>
      );

    case "success":
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="success-section"
        >
          <CheckCircleIcon className="success-icon" />
          <h1>¡Registro Exitoso!</h1>
          <h3>
            {recordType === "entrada" ? "Entrada" : "Salida"} registrada
            correctamente
          </h3>

          <div className="success-details">
            <div className="time-badge">
              <AccessTimeIcon />
              <span>{new Date().toLocaleTimeString("es-CO")}</span>
            </div>
          </div>

          <p className="farewell-message">
            {recordType === "entrada"
              ? "¡Que tenga un excelente día!"
              : "¡Gracias por su visita!"}
          </p>

          <p className="redirect-message">
            Será redirigido automáticamente en 5 segundos...
          </p>
        </motion.div>
      );

    default:
      return null;
  }
};
  // Mostrar pantalla de carga
  if (isLoading) {
    return (
      <div className="attendance-loading">
        <div className="spinner-container">
          <div className="spinner" />
          <h3>Verificando código QR...</h3>
        </div>
      </div>
    );
  }

  // Mostrar error si el token no es válido
  if (!canProceed) {
    return (
      <div className="attendance-error">
        <div className="error-content">
          <WarningIcon className="error-icon" />
          <h2>Código QR no válido</h2>
          <p>{tokenError || "El código QR no es válido o ha expirado"}</p>
          <button
            className="btn btn-primary"
            onClick={() => (window.location.href = "/")}
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance-container">
      <div className="attendance-layout">
        {/* Sidebar con stepper */}
        <AttendanceStepper
          currentStep={currentStep}
          steps={steps}
          isExit={recordType === "salida"}
        />

        {/* Contenido principal */}
        <div className="attendance-content">
          <AnimatePresence mode="wait">
            {currentStep !== "success" ? (
              <motion.div key={currentStep} className="content-wrapper">
                {renderStepContent()}
              </motion.div>
            ) : (
              renderStepContent()
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AttendanceQR;
